
import { Component, Vue, Prop, Inject } from "vue-property-decorator"
import { Getter } from "vuex-class"
import RecordAssignmentService from "@/services/recordAssignmentService"
import { AssignedHours } from "@/types/assignment"
import { CaseDetails, CaseField } from "@/types"
import { TenantFeatures } from "@/types/tenantFeatures"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { ConvertTimeUnit, SingularTimeUnit, TimeUnit } from "@/types/time"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"
import { hasViewPermission } from "@/scripts/caseFields"
import { CurrentUser } from "@/types/user"

@Component({
	components: {
		"help-icon": HelpIcon
	}
})
export default class AssignedTime extends Vue {
	@Inject({ from: "recordAssignmentService", default: null }) recordAssignmentService!: RecordAssignmentService

	// --- Props ---

	@Prop({ required: true })
	currentCase!: CaseDetails

	@Prop({ required: true })
	field!: CaseField

	// --- Vuex ---

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getMe") me!: CurrentUser

	// --- Computed Properties ---

	/**
	 * Get list of fields that actively use this field for hours.
	 * @returns {CaseField[]} list of CaseFields
	 */
	get activeFieldsForHours (): CaseField[] {
		const recordUserRoleFields = Object.values(this.currentCase.fields)
			.filter(f => f.field.dataType === "user-role")

		return recordUserRoleFields.filter(f =>
			this.recordAssignmentService
				.getTimeManagementHoursForRecord(f.field, this.currentCase) === this.field.field.id
		)
	}

	/**
	 * Map list of fields to set of field IDs
	 * @returns {string[]} List of field IDs
	 */
	get viewableActiveFieldsForHours (): CaseField[] {
		return this.activeFieldsForHours.filter(f => hasViewPermission(f, this.me, this.currentCase))
	}

	/**
	 * Get assigned hours details based on this field
	 * @returns {AssignedHours | null} Assigned hours summary, or null if no data is set
	 */
	get assignedHours (): AssignedHours | null {
		return this.recordAssignmentService.hourFieldAssignments[this.field.field.id!] ?? null
	}

	/**
	 * Gets the value of this field, or zero if unset
	 * @returns {number} Hours value set
	 */
	get time (): number {
		return this.field.value ?? 0
	}

	/**
	 * Gets the total assigned hours for this field
	 * @returns {number} Assigned hours
	 */
	get assignedTime (): number {
		return this.assignedHours?.assignedTime ?? 0
	}

	/**
	 * Gets the total unassigned hours for this field and any other linked fields
	 * @returns {number} Unassigned hours
	 */
	get unassignedTime (): number {
		return this.assignedHours?.unassignedTime ?? 0
	}

	/**
	 * Returns true the UserRoleTimeAsHoursMinutes tenant feature is enabled
	 */
	get displayTimeInHoursAndMinutes (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.UserRoleTimeAsHoursMinutes)
	}

	get displayAssignedTime (): string {
		return this.displayTimeInHoursAndMinutes
			? this.getTimeHoursAndMinutesDisplay(this.assignedTime)
			: this.assignedTime.toFixed(2)
	}

	get displayUnassignedTime (): string {
		return this.displayTimeInHoursAndMinutes
			? this.getTimeHoursAndMinutesDisplay(this.unassignedTime)
			: this.unassignedTime.toFixed(2)
	}

	/**
	 * Over assigned hours for hour field
	 */
	get displayOverassignedTime (): string {
		if (!this.assignedHours || !this.isOverAssigned) {
			return ""
		}

		const overAssignedTime = this.assignedHours.assignedTime - this.assignedHours.hours
		return this.displayTimeInHoursAndMinutes
			? this.getTimeHoursAndMinutesDisplay(overAssignedTime)
			: overAssignedTime.toFixed(2)
	}

	/**
	 * Returns true if the hour field is over assigned (without taking into account pool of hours)
	 */
	get isOverAssigned (): boolean {
		return !!this.assignedHours && (this.assignedHours.assignedTime > this.assignedHours.hours)
	}

	/**
	 * Helper for displaying time in hours and minutes.
	 * @param timeValueHrs Time value in hours.
	 */
	private getTimeHoursAndMinutesDisplay (timeValueHrs: number): string {
		const timeValueMs = ConvertTimeUnit(timeValueHrs, TimeUnit.Hours, TimeUnit.Milliseconds)
		return formatDateMixin.methods.getFormattedTimeSpanDisplay(timeValueMs, {
			includeDelimiter: false,
			shortenedUnits: true,
			greatestUnit: TimeUnit.Hours,
			smallestUnit: TimeUnit.Minutes,
			roundToClosest: SingularTimeUnit.Second,
			belowThresholdTextOverride: "0 hrs"
		})
	}
}

